import './style/Home.css';
import { useEffect, useRef } from 'react';
import PublicNav from '../components/PublicNav';
import banner from '../images/banner.png';
import testsIcon from '../images/tests.png';
import arenaIcon from '../images/arena.png';

function Home(props) {
  const modesRef = useRef(null);
  const aboutRef = useRef(null);
  const faqRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, { threshold: 0.9 });
    const aboutIntro = aboutRef.current?.querySelector('.intro');
    if (aboutIntro) observer.observe(aboutIntro);
    aboutRef.current?.querySelectorAll('.feature-card').forEach((card) => observer.observe(card));
    const modesTitle = modesRef.current?.querySelector('h3');
    if (modesTitle) observer.observe(modesTitle);
    modesRef.current?.querySelectorAll('.mode-block').forEach((block) => observer.observe(block));
    const faqTitle = faqRef.current?.querySelector('h3');
    if (faqTitle) observer.observe(faqTitle);
    faqRef.current?.querySelectorAll('.item').forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  return (
    <div className='home-container'>
      <PublicNav showFeatureNav={true} user={props.user} />
      <div className='landing-section'>
          <img src={banner} alt='πPrep' className='banner' />
          <h2>Math Adapted to You</h2>
      </div>
      <div id="about" ref={aboutRef}>
        <div className='intro'>
          <h3>What is πPrep?</h3>
          <p>
            The smarter way to study for SAT Math.
          </p>
        </div>
        <div className='features'>
          <div className='feature-card'>
            <span className='feature-icon'>🎯</span>
            <h4>Study Your Way</h4>
            <p>Quick-fire practice or full-length exams. Pick what fits, switch anytime, and never feel locked into one style of studying.</p>
          </div>
          <div className='feature-card'>
            <span className='feature-icon'>⚡</span>
            <h4>Built-In Explanations</h4>
            <p>Every problem comes with a full explanation so you learn the reasoning, not just the answer. No more wondering where you went wrong.</p>
          </div>
          <div className='feature-card'>
            <span className='feature-icon'>📊</span>
            <h4>Progress Tracking</h4>
            <p>Track your rating, accuracy, and growth across all four math domains. Watch yourself improve in real time, not just on test day.</p>
          </div>
          <div className='feature-card'>
            <span className='feature-icon'>🧪</span>
            <h4>Realistic Tests</h4>
            <p>Timed sections, question flagging, and full reviews built to mirror the real thing. Walk in confident, not guessing what to expect.</p>
          </div>
        </div>
      </div>
      <div id="modes" ref={modesRef}>
        <h3>Two Ways to Practice</h3>
        <div className='mode-block slide-from-left'>
          <div className='mode-icon'>
            <img src={arenaIcon} alt='Arena' className='mode-img arena-img' />
            <h4>Arena</h4>
          </div>
          <div className='mode-content'>
            <p>Every question fights back. Arena matches you against problems at your exact skill level. Answer right and they get harder, miss and they adjust.</p>
            <ul>
              <li>Questions dynamically adapt to your current rating</li>
              <li>Get instant feedback with explanations and hints</li>
              <li>Skip questions and come back when you're ready</li>
              <li>Track your progress across every math domain</li>
              <li>Compete on the leaderboard with other students</li>
              <li>Build consistency with unlimited practice sessions</li>
            </ul>
          </div>
        </div>
        <div className='mode-block reverse slide-from-right'>
          <div className='mode-content'>
            <p>Practice on your terms. Take full-length SAT Math sections at your own pace or turn on the clock to simulate the real exam.</p>
            <ul>
              <li>Timed sections that match real SAT structure</li>
              <li>Flag, pause, and revisit any question freely</li>
              <li>Instant scoring with full performance breakdowns</li>
              <li>Review every answer with detailed explanations</li>
              <li>Choose timed or untimed mode to fit your needs</li>
              <li>Track your scores over time to see improvement</li>
            </ul>
          </div>
          <div className='mode-icon'>
            <img src={testsIcon} alt='Tests' className='mode-img tests-img' />
            <h4>Tests</h4>
          </div>
        </div>
      </div>
      <div id="faq" ref={faqRef}>
        <h3>Frequently Asked Questions</h3>
        <div className='list'>
          <div className='item'>
            <h4>Is πPrep free to use?</h4>
            <p>Completely free during the alpha. Arena, practice tests, stats, all of it. No paywall, no trial period.</p>
          </div>
          <div className='item'>
            <h4>How does the Arena work?</h4>
            <p>You tackle one question at a time, drawn from a pool tailored to your rating. Get it right and your rating climbs. Get it wrong and it dips. Hints are available but come with a penalty, so there's real incentive to push through on your own. As your rating grows, the question pool shifts toward harder problems.</p>
          </div>
          <div className='item'>
            <h4>Are the practice tests timed?</h4>
            <p>That's up to you. Each test can be taken with or without a timer. Either way, you get the same full-length format and a detailed score breakdown when you're done.</p>
          </div>
          <div className='item'>
            <h4>How do I track my progress?</h4>
            <p>Your dashboard tracks rating history, accuracy trends, and domain-level breakdowns. Everything updates automatically after each Arena question or completed test.</p>
          </div>
        </div>
      </div>
      <footer className='footer'>
        Created by Ignacio Bigott
      </footer>
    </div>
  );
}

export default Home;